import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Inbox, Mail, Phone, MapPin, Check, Archive, TrendingUp } from "lucide-react";
import { toast } from "sonner";

/** Coach/admin-only inbox of leads submitted via the public /contact form. */
export default function LeadsInbox() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("new"); // new | contacted | signed_up | archived | all
  const [expanded, setExpanded] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const r = await api.get(`/leads${q}`);
      setLeads(r.data || []);
    } catch (e) {
      // If forbidden, quietly hide.
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const r = await api.get("/leads/funnel/metrics?days=7");
      setMetrics(r.data);
    } catch {
      setMetrics(null);
    }
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => { loadMetrics(); }, []);

  const mark = async (id) => {
    try {
      await api.post(`/leads/${id}/mark-contacted`);
      toast.success("Marked as contacted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };
  const archive = async (id) => {
    try {
      await api.post(`/leads/${id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive");
    }
  };

  const counts = {
    new: leads.filter(() => filter === "new").length,
  };

  return (
    <div className="obs-card" data-testid="leads-inbox">
      <div className="flex items-center gap-3 mb-3">
        <Inbox className="h-5 w-5 text-blaze" />
        <div className="flex-1">
          <div className="overline">New business</div>
          <h3 className="font-heading text-2xl uppercase">Inbound leads</h3>
        </div>
      </div>

      {metrics && metrics.leads > 0 && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="leads-funnel-metrics">
          <FunnelTile label="Leads · 7d" value={metrics.leads} />
          <FunnelTile
            label="Signed up"
            value={metrics.signed_up}
            sub={`${metrics.signup_rate_pct}%`}
            highlight={metrics.signup_rate_pct >= 50}
          />
          <FunnelTile
            label="Subscribed"
            value={metrics.subscribed}
            sub={`${metrics.subscribe_rate_pct}%`}
            highlight={metrics.subscribe_rate_pct >= 50}
          />
          <FunnelTile
            label="End-to-end"
            value={`${metrics.end_to_end_pct}%`}
            sub="lead → paid"
            trendIcon
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="leads-filter-row">
        {[
          { k: "new", label: "New" },
          { k: "contacted", label: "Contacted" },
          { k: "signed_up", label: "Signed up" },
          { k: "archived", label: "Archived" },
          { k: "all", label: "All" },
        ].map((f) => (
          <button
            key={f.k}
            type="button"
            onClick={() => setFilter(f.k)}
            data-testid={`leads-filter-${f.k}`}
            className={`text-[10px] uppercase tracking-widest px-3 py-1 border rounded-full ${
              filter === f.k
                ? "bg-blaze text-obsidian-900 border-blaze"
                : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : leads.length === 0 ? (
        <p className="text-zinc-500 text-sm">
          No {filter === "all" ? "" : filter} leads yet.{" "}
          Share your contact link:{" "}
          <a href="/contact" target="_blank" rel="noreferrer" className="text-blaze underline">
            {typeof window !== "undefined" ? `${window.location.origin}/contact` : "/contact"}
          </a>
        </p>
      ) : (
        <div className="space-y-2">
          {leads.map((l) => {
            const isOpen = expanded === l.id;
            return (
              <div
                key={l.id}
                className="border border-obsidian-600 rounded-sm p-3"
                data-testid={`lead-row-${l.id}`}
              >
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : l.id)}
                    className="flex-1 text-left"
                  >
                    <div className="font-heading text-lg uppercase truncate">{l.full_name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">
                      {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      {" · "}{l.email}
                      {l.state ? ` · ${l.state}` : ""}
                    </div>
                  </button>
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-full ${
                      l.status === "new"
                        ? "border-blaze text-blaze"
                        : l.status === "contacted"
                        ? "border-emerald-500/50 text-emerald-400"
                        : l.status === "signed_up"
                        ? "border-emerald-500 text-emerald-300 bg-emerald-500/10"
                        : "border-obsidian-600 text-zinc-500"
                    }`}
                  >
                    {l.status === "signed_up" ? "signed up" : l.status}
                  </span>
                </div>

                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-obsidian-600 space-y-2 text-sm">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      <span className="inline-flex items-center gap-1 text-zinc-300">
                        <Mail className="h-3 w-3 text-zinc-500" />
                        <a href={`mailto:${l.email}`} className="hover:text-blaze">{l.email}</a>
                      </span>
                      {l.phone && (
                        <span className="inline-flex items-center gap-1 text-zinc-300">
                          <Phone className="h-3 w-3 text-zinc-500" />
                          <a href={`tel:${l.phone}`} className="hover:text-blaze">{l.phone}</a>
                        </span>
                      )}
                      {l.state && (
                        <span className="inline-flex items-center gap-1 text-zinc-300">
                          <MapPin className="h-3 w-3 text-zinc-500" /> {l.state}
                        </span>
                      )}
                      {(l.age != null || l.gender) && (
                        <span className="text-zinc-400">
                          {l.age != null ? `${l.age} yr` : ""}{l.age != null && l.gender ? " · " : ""}
                          {l.gender ? l.gender.replace(/_/g, " ") : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-zinc-300 whitespace-pre-wrap text-sm">{l.goals}</div>
                    <div className="flex gap-2 pt-1">
                      {l.status !== "contacted" && (
                        <button
                          type="button"
                          onClick={() => mark(l.id)}
                          data-testid={`lead-contacted-${l.id}`}
                          className="blaze-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-2"
                        >
                          <Check className="h-3 w-3" /> Mark contacted
                        </button>
                      )}
                      {l.status !== "archived" && (
                        <button
                          type="button"
                          onClick={() => archive(l.id)}
                          data-testid={`lead-archive-${l.id}`}
                          className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-2"
                        >
                          <Archive className="h-3 w-3" /> Archive
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FunnelTile({ label, value, sub, highlight, trendIcon }) {
  return (
    <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
      <div className="flex items-center justify-between">
        <div className="overline text-[9px]">{label}</div>
        {trendIcon && <TrendingUp className="h-3 w-3 text-blaze" />}
      </div>
      <div className={`font-data text-2xl mt-1 ${highlight ? "text-blaze" : "text-white"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{sub}</div>}
    </div>
  );
}
